/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient.js";
import { claimMyInvitations, claimMyPlatformAdminInvitations } from "../utils/accountService.js";

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // Claim pending invites once per signed-in user (fire-and-forget): workspace
  // membership, then any Brand Command staff (platform_admins) grant.
  const claimedFor = useRef(null);
  function claimInvitesOnce(s) {
    const uid = s?.user?.id;
    if (uid && claimedFor.current !== uid) {
      claimedFor.current = uid;
      claimMyInvitations().catch(() => {});
      claimMyPlatformAdminInvitations().catch(() => {});
    }
  }

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setLoading(false);
      claimInvitesOnce(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null);
      claimInvitesOnce(s);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = {
    session,
    user: session?.user ?? null,
    loading,
    signOut: () => supabase.auth.signOut(),
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be inside SessionProvider");
  return ctx;
}
